<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="true"
                :showAddNew="false"
                :title="invoiceId ? 'Edit Investigation Bill' : 'Investigation Bill'"
                @onClickCloseButton="goToList"
            />
        </div>

        <div class="my-2 px-2">
            <TableHeaderHospitalBillAdd
                :key="componentKey"
                :formData="formData"
                :tableItems="tableItems"
                :resourceType="homeContent.resource_type"
                :resourceSpecifications="homeContent.resource_specifications"
                :genders="homeContent.genders"
                :accountHeads="accountHeads"
                :serviceResourceRes="serviceResourceRes"
                :referrerRes="referrerRes"
                :clientRes="clientRes"
            />
        </div>

        <div class="col-12 px-2">
            <ListTable 
                :tableItems="tableItems"
                :salesAccountHeads="salesAccountHeads"
                :maxHospitalDiscountPercentage="homeContent.max_hospital_discount_percentage"
                :cashAndBank="cashAndBank"
                ref="itemDetailsData"
            />
        </div>
        <div class="row px-2">
            <div class="col-md-6"></div>
            <div class="col-md-5 ml-auto mt-auto mb-1">
                <div class="d-flex flex-wrap gap-1 gy-2">
                    <button :disabled="loading" @click="handleSubmit(false)" class="btn btn-primary">{{ invoiceId ? 'Update' : 'Save' }}</button>
                    <button :disabled="loading" @click="handleSubmit(true)" class="btn btn-primary">{{ invoiceId ? 'Update & Print' : 'Save & Print' }}</button>
                </div>
            </div>
        </div>
        
    </div>
</template>

<script setup>
import {inject, onMounted, computed, provide, reactive, ref} from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import ListTable from '@/components/molecule/company/hospital/billing/investigation/dc/InvoiceHospitalTable.vue';
import {useRoute, useRouter} from 'vue-router';
import handlePurchase from '@/services/modules/purchase';
import handleCompany from "@/services/modules/company";
import hospitalPdfPrinterHelper from '@/services/utils/hospitalPdfPrinterHelper';
import TableHeaderHospitalBillAdd from "@/components/molecule/company/hospital/billing/investigation/dc/InvestigationTableHead.vue";
import useDate from "@/services/utils/day";
import handleInventory from "@/services/modules/inventory";
import handleDropdowns from '@/services/modules/dropdowns';

    const { generatePdf } = hospitalPdfPrinterHelper();
    const { fetchProductList, fetchSalesBill, ...rest } = handlePurchase();
    const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany();
    const { fetchHome } = handleInventory();
    const { fetchDropdownList } = handleDropdowns();

    const $route = useRoute();
    const $router = useRouter();
    const date = useDate();

    const companyId = $route.params.companyId;
    const loader = ref(true);
    const salesAccountHeads = ref([]);
    const totals = ref({});
    const invoiceRes = ref({});
    const itemDetailsData = ref(null);
    const company = ref({});
    const componentKey = ref(0);
    const loading = ref(false);
    const customizationPdfFields = ref({});
    const homeContent = ref({});
    const cashAndBank = ref([]);
    const accountHeads = ref([]);
    const serviceResourceRes = ref({});
    const referrerRes = ref({});
    const clientRes = ref({});

    const invoiceId = computed(() => $route.params.invoiceId)

    let formData = ref({
        company_id: companyId,
        contact_profile_id: null,
        referrer_id: null,
        account_head_id: null,
        ipd_register_id: null,
        vat_payable_account_head_id: null,
        service_resource_id: null,
        status: 'active',
        mop_account_head_id: null,
        contact_profile_client_id: null,
        transport_reference: null,
        receipt_reference: null,
        discount_head_id: null,
        adjustment_amount: 0,
        referrer_discount_amount: 0,
        receipt_description: "Invoice bill payment",
        payment: null,
        date: '',
        has_item_detail: true,
        description: '',
        order_number: null,
        lc_number: null,
        item_details: []
    });

    provide('formData', formData);
    provide('totals', totals);
    const showSuccess =  inject('showSuccess');
    const showError =  inject('showError');

    onMounted( async() => {
        const dropdownRes = fetchDropdownList(getDropDownQuery());
        const homeRes = fetchHome();
        const fetchCompany = fetchCompanyInfo(companyId)

        Promise.all([
            dropdownRes.then(res => {
              if(! res.status) return;

              accountHeads.value = res.data.account_heads;

              if(res.data.account_head_list_by_slag.accounts_receivable) {
                formData.value.account_head_id = res.data.account_head_list_by_slag.accounts_receivable[0].id;
              }
          
              if(res.data.account_head_list_by_slag.vat_payable) {
                formData.value.vat_payable_account_head_id = res.data.account_head_list_by_slag.vat_payable[0].id;
              }
          
              if(res.data.account_head_list_by_slag.discount_account) {
                salesAccountHeads.value = res.data.account_head_list_by_slag.discount_account;
                formData.value.discount_head_id = res.data.account_head_list_by_slag.discount_account[0].id;
              }
          
              if(res.data.account_head_list_by_slag.cash_and_banks) {
                cashAndBank.value = res.data.account_head_list_by_slag.cash_and_banks;
                formData.value.mop_account_head_id = cashAndBank.value[0].id;
              }
            }),
            homeRes.then(res=> {
                if(res.data) homeContent.value = res.data;
            }),
            fetchCompany.then(res=> {
                if(res.data) company.value = res.data;
            }),
            await getSalesBill(),
            await getActiveFiledCustomization(),
        ])
        .then(() => {loader.value=false})
        .catch((e)=>{loader.value=false; console.log(e)})
    })

    const getDropDownQuery = () => {
      let query = `?company_id=${companyId}
        &is_account_heads=1
        &slags[]=discount_account
        &slags[]=vat_payable
        &slags[]=accounts_receivable
        &slags[]=cash_and_banks
        &is_all_account_heads=1`;

      return query;
    }

    let tableItems = reactive([]);

    const resetForm = () => {
        tableItems.splice(0);
        componentKey.value += componentKey.value + 1

        formData.value.company_id = companyId;
        formData.value.contact_profile_id = null;
        formData.value.contact_profile_client_id = null;
        formData.value.transport_reference = null;
        formData.value.referrer_id = null;
        formData.value.ipd_register_id = null;
        formData.value.service_resource_id = null;
        formData.value.status = 'active';
        formData.value.receipt_reference = null;
        formData.value.adjustment_amount = 0;
        formData.value.referrer_discount_amount = 0;
        formData.value.receipt_description = "Invoice bill payment";
        formData.value.payment = null;
        formData.value.date = '';
        formData.value.has_item_detail = true;
        formData.value.description = '';
        formData.value.order_number = null;
        formData.value.lc_number = null;
        formData.value.item_details = [];
        itemDetailsData.value.resetData();
    }

    const goToList = () => {

        if($route.query.contactId){
            $router.push({
                name: "patient-register",
                params: $route.params,
                query: $route.query,
            });
            return;
        }

        const routeName = invoiceId.value ? 'bill-manager' : 'investigation-bill-dc-list';

        $router.push({name: routeName,
            params: {
                companyId: $route.params.companyId,
                moduleId: $route.params.moduleId,
                menuId: $route.params.menuId,
                pageId: $route.params.pageId
            },
            query: {
                start: $route.query.start,
                end: $route.query.end
            }

        })
    }

    const handleSubmit = async (print = false) => {
        if (!formData.value.date) {
            formData.value.date = date.currentDate();
        }

        formData.value.item_details = tableItems;

        const payload = { ...formData.value };
        payload.item_details = tableItems;
        payload.sale_type = "investigation_invoice";
        payload.receipt_type = "investigation_invoice";

        loading.value = true;

        try {
            const isEdit = !!invoiceId.value;

            const res = isEdit
                ? await rest.updateSalesBill(invoiceId.value, payload)
                : await rest.storeBillPayment(payload);

            if (!res.status) {
                showError(res.message);
                return;
            }

            showSuccess(res.message);

            if (print) {
                const printId = isEdit ? invoiceId.value : res.data.id;
                goToPrint(printId);
            }

            if(isEdit) {
                return goToList();
            }

            resetForm();

        } catch (err) {
            showError(err?.response?.data?.message || 'Something went wrong');
        } finally {
            loading.value = false;
        }
    };

    const getActiveFiledCustomization = async () => {
      const companyQuery = `?company_id=${$route.params.companyId}&customization_type=pdf&type=investigation_bill_pdf`;
      try {
        const res = await fetchActiveCustomizationField(companyQuery);
        customizationPdfFields.value = res.data;
      } catch (error) {
        console.error("Error fetching customization fields:", error);
      }
    }

    const goToPrint = (id) => {
        const companyQuery = `?company_id=${companyId}`;

        fetchSalesBill(id, companyQuery).then( (res) =>{
            if( res ){
                invoiceRes.value = res.data;
            }
        }).then( () => {
            generatePdf(company.value, invoiceRes.value, 'BILL', true, true, false, true, false, customizationPdfFields.value)
        }).catch( (err) => {
            console.log(err);
        })
    }

    const getSalesBill = async () => {
        if(! invoiceId.value) return;
        const companyQuery = `?company_id=${companyId}`;

        try {
            const res = await fetchSalesBill(invoiceId.value, companyQuery);
            if(! res.status ) {
                showError(res.message)
                return;
            }
            setInitialFormData(res.data)
        } catch (error) {
            console.error("Error fetching sales bill:", error);
        }
    }

    const setInitialFormData = (data) => {
        formData.value.company_id = data.company_id;
        formData.value.bill_number = data.bill_number;
        formData.value.contact_profile_id = data.contact_profile_id;
        formData.value.account_head_id = data.account_head_id;
        formData.value.vat_payable_account_head_id = data.vat_payable_account_head_id;
        formData.value.referrer_id = data.referrer_id;
        formData.value.transport_reference = data.transport_reference;
        formData.value.discount_head_id = data.discount_head_id;
        formData.value.adjustment_amount = data.adjusted_amount ?? 0;
        formData.value.referrer_discount_amount = 0;
        formData.value.receipt_description = data.description;
        formData.value.payment = data.paid_amount;
        formData.value.date = data.date;
        formData.value.status = data.status;
        formData.value.has_item_detail = true;
        formData.value.description = data.description;
        formData.value.order_number = data.order_number;
        formData.value.lc_number = data.lc_number;
        formData.value.contact_profile_client_id = data.contact_profile_client_id;
        formData.value.total_discount = data.total_discount;

        formData.value.contact = data.contact;
        formData.value.company = data.company;

        if(data.service_resource_id) {
            formData.value.service_resource_id = data.service_resource_id
            serviceResourceRes.value = {
                id: data.service_resource_id,
                name: data.investigation_consultant ? data.investigation_consultant : ''
            }
        };

        if(data.referrer_id) {
            formData.value.referrer_id = data.referrer_id
            referrerRes.value = {
                id: data.referrer_id,
                name: data.referrer_name ? data.referrer_name : ''
            }
        };

        if(data.contact_profile_client_id) {
            formData.value.contact_profile_client_id = data.contact_profile_client_id
            clientRes.value = {
                id: data.contact_profile_client_id,
                name: data.contact_profile_client_name ? data.contact_profile_client_name : ''
            }
        };

        if(data.receipt_generals && data.receipt_generals.length) {
            formData.value.mop_account_head_id = data.receipt_generals[0].receipt_master ? 
                                        data.receipt_generals[0].receipt_master.account_head_id : 
                                        cashAndBank.value[0].id
        }

        tableItems.splice(0);
        data.general.forEach(item => {
            tableItems.push({
                id: item.id,
                sale_master_id: item.sale_master_id,
                product_id: item.product_id,
                name: item.product.name,
                description: item.product.description.unit.name,
                quantity: item.quantity,
                rate: item.rate,
                discount: item.discount_percent,
                vat: item.vat,
                sub_total: item.amount,
                discount_amount: item.discount_amount,
                discount_percent: item.discount_percent,
                vat_amount: item.vat_amount,
                total: item.total_amount,
                is_edit: true,
                product: {
                    id: item.product.id,
                    name: item.product.name
                }
            });
        });
    }

</script>
<style scoped>
address{
    margin-bottom: 0 !important;
}
</style>